// src/components/AGTUps/products/AGTCouncil-products/Recent.js

import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Grid, Chip, Avatar, 
  AvatarGroup, CircularProgress, Divider,
  Tooltip, IconButton, Card, useTheme,
  Modal, Button, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Clock, MessageCircle, ChevronRight, 
  ChevronDown, X, CheckCircle, Download
} from 'lucide-react';
import consultService from '../../services/AGTUps-product-services/consultService';

const StyledCard = styled(Card)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(2),
    height: '100%',
    maxWidth: '950px',
    boxShadow: isWallpaperMode ? 'none' : '0 4px 20px rgba(0, 0, 0, 0.05)',
    border: `1px solid ${isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : 'rgba(0, 0, 0, 0.08)'}`,
    transition: 'all 0.3s ease',
    background: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : theme.palette.background.paper,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    margin: '0 auto',
  };
});

const ConsultationCard = styled(Card)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    border: `1px solid ${isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : 'rgba(0, 0, 0, 0.08)'}`,
    cursor: 'pointer',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.background.paper,
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: theme.shadows[4],
    },
  };
});

const StatusChip = styled(Chip)(({ theme, status }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    borderRadius: theme.spacing(0.75),
    fontSize: '0.7rem',
    height: 24,
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : status === 'finalizado'
      ? theme.palette.success.light
      : theme.palette.primary.light,
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    color: status === 'finalizado'
      ? theme.palette.success.dark
      : theme.palette.primary.dark,
  };
});

const TagChip = styled(Chip)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    borderRadius: theme.spacing(0.75),
    fontSize: '0.7rem',
    height: 22,
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.grey[100],
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
  };
});

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

const ModalContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflowY: 'auto',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : theme.palette.background.paper,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    borderRadius: theme.spacing(2),
    boxShadow: isWallpaperMode ? 'none' : theme.shadows[5],
    padding: theme.spacing(4),
  };
});

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSender',
})(({ theme, isSender }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    maxWidth: '70%',
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.spacing(2),
    backgroundColor: isSender
      ? '#0385FF'
      : isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.mode === 'dark'
      ? '#464649'
      : '#F2F2F7',
    backdropFilter: !isSender && isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: !isSender && isWallpaperMode ? 'blur(8px)' : 'none',
    border: !isSender && isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    color: isSender ? '#FFFFFF' : theme.palette.text.primary,
    margin: theme.spacing(1),
    position: 'relative',
    alignSelf: isSender ? 'flex-end' : 'flex-start'
  };
});

const ConversationContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.mode === 'dark' ? '#1A1A1A' : '#F9F9F9',
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(2),
    maxHeight: '400px',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '4px',
    },
  };
});

// Styled buttons for wallpaper mode
const StyledPrimaryButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : '#0385FF',
    color: '#FFFFFF',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.4)' : '#0266cc'
    }
  };
});

const StyledOutlinedButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    borderColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    '&:hover': {
      borderColor: isWallpaperMode ? '#FFFFFF' : '#0266cc',
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(3, 133, 255, 0.08)'
    }
  };
});

const StyledIconButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    color: isWallpaperMode ? '#FFFFFF' : theme.palette.primary.main,
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(3, 133, 255, 0.08)'
    }
  };
});

const Recent = ({ maxWidth = '950px' }) => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [consultationDetails, setConsultationDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const theme = useTheme();

  // Formatear fecha
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Obtener las consultas recientes del usuario al cargar el componente
  useEffect(() => {
    const fetchConsultations = async () => {
      setLoading(true);
      try {
        const res = await consultService.getUserConsultations();
        if (res.success && res.data) {
          // Ordenar consultas por fecha más reciente primero
          const sortedConsultations = res.data.sort((a, b) => 
            new Date(b.updatedAt) - new Date(a.updatedAt)
          );
          setConsultations(sortedConsultations);
        }
      } catch (error) {
        console.error('Error fetching recent consultations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConsultations();
  }, []);

  // Abrir modal con detalles de la consulta
  const handleOpenConsultationDetails = async (consultation) => {
    setSelectedConsultation(consultation);
    setModalOpen(true);
    setDetailsLoading(true);
    
    try {
      // Si ya tenemos los datos de la conversación, no es necesario cargarlos de nuevo
      if (consultation.conversation && consultation.conversation.length > 0) {
        setConsultationDetails(consultation);
      } else {
        // Cargar detalles de la consulta si no tenemos la conversación completa
        const res = await consultService.getConsultationHistory(consultation.consultationId);
        if (res.success && res.data) {
          setConsultationDetails(res.data);
        }
      }
    } catch (error) {
      console.error('Error fetching consultation details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedConsultation(null);
    setConsultationDetails(null);
  };

  // Obtener nombre del remitente
  const getSenderName = (sender, experts) => {
    if (sender === 'user') return 'Tú';
    
    if (sender === 'assistant1' && experts?.[0]?.name) {
      return experts[0].name;
    } else if (sender === 'assistant1') {
      return 'Estratega';
    }
    
    if (sender === 'assistant2' && experts?.[1]?.name) {
      return experts[1].name;
    } else if (sender === 'assistant2') {
      return 'Analista';
    }
    
    if (sender === 'assistant3' && experts?.[2]?.name) {
      return experts[2].name;
    } else if (sender === 'assistant3') {
      return 'Asesor';
    }
    
    return 'Asistente';
  };

  return (
    <StyledCard sx={{ maxWidth }}>
      <HeaderBox>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Consultas Recientes
        </Typography>
        <Tooltip title="Ver todas">
          <StyledIconButton size="small">
            <ChevronRight size={20} />
          </StyledIconButton>
        </Tooltip>
      </HeaderBox>
      
      <Divider sx={{ mb: 3 }} />
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress size={28} />
        </Box>
      ) : consultations.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" my={4}>
          <MessageCircle size={40} color={theme.palette.text.disabled} />
          <Typography variant="body2" color="text.secondary" mt={1}>
            No hay consultas recientes.
          </Typography>
        </Box>
      ) : (
        consultations.map((consult, index) => (
          <ConsultationCard 
            key={index} 
            elevation={0}
            onClick={() => handleOpenConsultationDetails(consult)}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {consult.title || `Consulta del ${formatDate(consult.createdAt)}`}
                    </Typography>
                    <Box display="flex" alignItems="center" mt={0.5}>
                      <Clock size={14} style={{ marginRight: 6, color: theme.palette.text.secondary }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {formatDate(consult.updatedAt)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center">
                    <StatusChip 
                      label={consult.status === 'finalizado' ? 'Completada' : 'En proceso'} 
                      status={consult.status}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <AvatarGroup
                      max={3}
                      sx={{
                        '& .MuiAvatar-root': {
                          width: 28,
                          height: 28,
                          fontSize: '0.75rem',
                          border: `1px solid ${theme.palette.background.paper}`,
                        },
                      }}
                    >
                      {consult.expertPanel &&
                        consult.expertPanel.map((expert, idx) => (
                          <Tooltip key={idx} title={expert.name || 'Experto'}>
                            <Avatar
                              sx={{
                                bgcolor: idx === 0 
                                  ? theme.palette.primary.light 
                                  : idx === 1 
                                    ? theme.palette.secondary.light 
                                    : theme.palette.error.light,
                                color: idx === 0 
                                  ? theme.palette.primary.dark 
                                  : idx === 1 
                                    ? theme.palette.secondary.dark 
                                    : theme.palette.error.dark,
                              }}
                            >
                              {expert.name ? expert.name.substring(0, 2).toUpperCase() : '??'}
                            </Avatar>
                          </Tooltip>
                        ))}
                    </AvatarGroup>
                  </Box>
                </Box>
                
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: theme.palette.grey[50],
                    mb: 1.5,
                    borderLeft: `3px solid ${theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.light}`,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {consult.conversation && consult.conversation.length > 0 && consult.conversation[0].messages && consult.conversation[0].messages.length > 0
                      ? consult.conversation[0].messages.find(msg => msg.sender === 'user')?.message.substring(0, 80) + '...'
                      : 'No hay conversación disponible.'}
                  </Typography>
                </Box>
                
                <Box display="flex" gap={1} flexWrap="wrap">
                  {consult.tags && consult.tags.map((tag, idx) => (
                    <TagChip key={idx} size="small" label={tag} />
                  ))}
                  <TagChip 
                    size="small" 
                    label={`${consult.conversation?.reduce((total, round) => total + round.messages.length, 0) || 0} mensajes`} 
                    icon={<MessageCircle size={12} />}
                  />
                </Box>
              </Grid>
            </Grid>
          </ConsultationCard>
        ))
      )}

      {/* Modal para mostrar detalles de la consulta */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="consultation-details-modal"
      >
        <ModalContainer>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              {selectedConsultation?.title || `Consulta del ${selectedConsultation ? formatDate(selectedConsultation.createdAt) : ''}`}
            </Typography>
            <StyledIconButton onClick={handleCloseModal} size="small">
              <X size={20} />
            </StyledIconButton>
          </Box>

          {detailsLoading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : consultationDetails ? (
            <>
              {/* Información de la consulta */}
              <Box 
                sx={{ 
                  mb: 3, 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)' 
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Fecha de consulta
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDate(consultationDetails.createdAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Estado
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <StatusChip 
                        label={consultationDetails.status === 'finalizado' ? 'Completada' : 'En proceso'} 
                        status={consultationDetails.status}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {consultationDetails.status === 'finalizado' && (
                        <CheckCircle size={16} color={theme.palette.success.main} style={{ marginLeft: 4 }} />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Panel de expertos */}
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Panel de Expertos
              </Typography>
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  mb: 3,
                  flexWrap: 'wrap'
                }}
              >
                {consultationDetails.expertPanel?.map((expert, idx) => (
                  <Box 
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: idx === 0 
                          ? theme.palette.primary.main 
                          : idx === 1 
                            ? theme.palette.secondary.main 
                            : theme.palette.error.main,
                        width: 32,
                        height: 32,
                        mr: 1
                      }}
                    >
                      {expert.name ? expert.name.substring(0, 2).toUpperCase() : '??'}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {expert.name || 'Experto'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {expert.role}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Conversación */}
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                Conversación
              </Typography>
              
              {consultationDetails.conversation && consultationDetails.conversation.length > 0 ? (
                <Accordion defaultExpanded sx={{ mb: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                  <AccordionSummary expandIcon={<ChevronDown />}>
                    <Typography>
                      Consulta completa ({consultationDetails.conversation.reduce((total, round) => total + round.messages.length, 0)} mensajes)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <ConversationContainer>
                      {consultationDetails.conversation.flatMap((round, roundIndex) =>
                        round.messages.map((msg, msgIndex) => {
                          const isSender = msg.sender === 'user';
                          const senderName = getSenderName(msg.sender, consultationDetails.expertPanel);
                          
                          return (
                            <Box 
                              key={`${roundIndex}-${msgIndex}`} 
                              sx={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: isSender ? 'flex-end' : 'flex-start',
                                mb: 1.5
                              }}
                            >
                              <Typography 
                                variant="caption" 
                                sx={{ ml: isSender ? 0 : 1, mr: isSender ? 1 : 0, mb: 0.5 }}
                              >
                                {senderName}
                              </Typography>
                              <MessageBubble isSender={isSender}>
                                <Typography variant="body2">
                                  {msg.message}
                                </Typography>
                                <Typography variant="caption" display="block" align="right" sx={{ mt: 1, opacity: 0.7 }}>
                                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                              </MessageBubble>
                            </Box>
                          );
                        })
                      )}
                    </ConversationContainer>
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No hay conversación disponible para esta consulta.
                </Typography>
              )}

              {/* Botón para descargar o compartir */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <StyledOutlinedButton
                  variant="outlined"
                  startIcon={<Download />}
                  sx={{ mr: 2 }}
                >
                  Exportar consulta
                </StyledOutlinedButton>
                <StyledPrimaryButton variant="contained">
                  Programar nueva consulta
                </StyledPrimaryButton>
              </Box>
            </>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No se pudo cargar la información de la consulta.
            </Typography>
          )}
        </ModalContainer>
      </Modal>
    </StyledCard>
  );
};

export default Recent;